import { Box, Flex, HStack, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { Link } from 'react-router-dom';
import { SquareArrowOutUpRight } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';

type Props = {
  title: string;
  titleIcon?: React.ReactElement;
  titleColor?: string;
  subTitle?: string;
  subTitleColour?: string;
  total: number;
  icons?: React.ReactElement[];
  to?: string;
  data: {
    label: string;
    color: string;
    percent: number;
    count: number;
  }[];
};

const BAR_HEIGHT = '0.5rem';
const MIN_LABEL_WIDTH = '5.5rem';

export default function ProgressBarCard({
  title,
  titleIcon,
  titleColor,
  subTitle,
  subTitleColour,
  total,
  data,
  icons,
  to,
}: Props) {
  const numberColumnWidth = `${total.toString().length + 1}ch`;

  return (
    <Box p={2} bg="bg.subtle" rounded="md" w="full">
      <HStack
        spacing={0}
        mt="1"
        mb="4"
        align="flex-start"
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex direction="column" gap="0.5">
          <HStack spacing={1} align="center">
            {titleIcon !== undefined && titleIcon}
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color={titleColor !== undefined ? titleColor : 'fg.subtle'}
            >
              {title}
            </Text>
          </HStack>

          {subTitle !== undefined ? (
            <Text
              fontSize="2xs"
              fontWeight="medium"
              color={subTitleColour !== undefined ? subTitleColour : 'fg.subtle'}
            >
              {subTitle}
            </Text>
          ) : null}
        </Flex>

        {to !== undefined ? (
          <Link to={to} color="fg.subtle">
            <SquareArrowOutUpRight size={iconSize.xs} />
          </Link>
        ) : null}
      </HStack>

      {data.map((item) => (
        <Flex key={item.label} align="center" mb={icons && icons.length > 0 ? 5 : 3}>
          <HStack minW={MIN_LABEL_WIDTH} gap={1}>
            {icons && icons.length > 0 ? icons[data.indexOf(item)] : null}

            <Text fontSize="xs" color="fg.subtle">
              {item.label}
            </Text>
          </HStack>
          <Flex justify="flex-end" flex="1">
            <Box bg={item.color} borderRadius="full" h={BAR_HEIGHT} w={`${item.percent}%`} />
          </Flex>
          <Text
            w={numberColumnWidth}
            minW="1ch"
            fontSize="2xs"
            textAlign="right"
            color={item.color}
          >
            {item.count}
          </Text>
        </Flex>
      ))}

      <Flex align="center" mt={4}>
        <Text fontSize="sm" minW={MIN_LABEL_WIDTH}>
          <Trans>Total</Trans>
        </Text>
        <HStack flex="1" spacing={0} borderRadius="full" overflow="hidden">
          {data.map((item) => (
            <Box key={item.label} bg={item.color} h={BAR_HEIGHT} w={`${item.percent}%`} />
          ))}
        </HStack>
        <Text fontSize="2xs" w={numberColumnWidth} minW="1ch" color="fg.muted" textAlign="right">
          {total}
        </Text>
      </Flex>
    </Box>
  );
}
